'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useWallet } from '@/lib/wallet/WalletContext';
import { computeConnectionId, splitSignature } from '@/lib/hyperliquid/service';
import type { SLTPState, TradeMarker } from './TradingDashboardInteractive';

interface TradingPanelProps {
  symbol: string;
  currentPrice: string;
  externalPrice?: string;
  externalSize?: string;
  externalSide?: 'Buy' | 'Sell';
  externalOrderType?: 'Market' | 'Limit' | 'Pro';
  sltp?: SLTPState;
  onSltpChange?: (sltp: SLTPState) => void;
  onTradePlaced?: (marker: TradeMarker) => void;
  usdcBalance?: number | null;
}

type OrderType = 'Market' | 'Limit' | 'Pro';
type Side = 'Buy' | 'Sell';
type MarginMode = 'Cross' | 'Isolated';

const PERCENTAGES = [25, 50, 75, 100];
const MAX_LEVERAGE = 50;
const MIN_LEVERAGE = 1;

function getRiskLabel(lev: number): { label: string; color: string } {
  if (lev <= 3) return { label: 'Low Risk', color: '#22C55E' };
  if (lev <= 10) return { label: 'Medium', color: '#EAB308' };
  if (lev <= 25) return { label: 'High Risk', color: '#F97316' };
  return { label: 'Extreme', color: '#EF4444' };
}

function getLeverageTrackColor(lev: number): string {
  const pct = (lev - MIN_LEVERAGE) / (MAX_LEVERAGE - MIN_LEVERAGE);
  if (pct <= 0.33) {
    const t = pct / 0.33;
    const r = Math.round(34 + (234 - 34) * t);
    const g = Math.round(197 + (179 - 197) * t);
    const b = Math.round(94 + (8 - 94) * t);
    return `rgb(${r},${g},${b})`;
  } else if (pct <= 0.66) {
    const t = (pct - 0.33) / 0.33;
    const r = Math.round(234 + (249 - 234) * t);
    const g = Math.round(179 + (115 - 179) * t);
    const b = Math.round(8 + (22 - 8) * t);
    return `rgb(${r},${g},${b})`;
  } else {
    const t = (pct - 0.66) / 0.34;
    const r = Math.round(249 + (239 - 249) * t);
    const g = Math.round(115 + (68 - 115) * t);
    const b = Math.round(22 + (68 - 22) * t);
    return `rgb(${r},${g},${b})`;
  }
}

// Thumb half-width in px — used to clamp so thumb never overflows track edges
const THUMB_HALF_W = 14;

// ─── Clean Leverage Slider ────────────────────────────────────────────────────

const TICK_COUNT = 9; // number of tick marks (evenly spaced)

function CleanLeverageSlider({ leverage, onChange }: { leverage: number; onChange: (v: number) => void }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const ratio = (leverage - MIN_LEVERAGE) / (MAX_LEVERAGE - MIN_LEVERAGE);

  const getValueFromClientX = useCallback((clientX: number): number => {
    if (!trackRef.current) return leverage;
    const rect = trackRef.current.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const clamped = Math.max(0, Math.min(rect.width, offsetX));
    const r = clamped / rect.width;
    return Math.round(MIN_LEVERAGE + r * (MAX_LEVERAGE - MIN_LEVERAGE));
  }, [leverage]);

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onChange(getValueFromClientX(e.clientX));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    const handleMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      onChange(getValueFromClientX(ev.clientX));
    };
    const handleUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    isDragging.current = true;
    const handleMove = (ev: TouchEvent) => {
      if (!isDragging.current || !ev.touches[0]) return;
      onChange(getValueFromClientX(ev.touches[0].clientX));
    };
    const handleEnd = () => {
      isDragging.current = false;
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
  };

  return (
    <div className="px-0 py-1 select-none">
      {/* Slider track area */}
      <div
        ref={trackRef}
        className="relative w-full cursor-pointer"
        style={{ height: '20px', display: 'flex', alignItems: 'center' }}
        onClick={handleTrackClick}
      >
        {/* Background rail */}
        <div
          className="absolute inset-x-0"
          style={{ height: '2px', top: '50%', transform: 'translateY(-50%)', background: '#3a3a3a', borderRadius: 0 }}
        />
        {/* Blue fill */}
        <div
          className="absolute pointer-events-none"
          style={{
            height: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
            left: 0,
            width: `${ratio * 100}%`,
            background: '#4c6ef4',
            borderRadius: 0,
          }}
        />
        {/* Tick marks */}
        {Array.from({ length: TICK_COUNT }).map((_, i) => {
          const tickRatio = i / (TICK_COUNT - 1);
          const isFilled = tickRatio <= ratio;
          return (
            <div
              key={i}
              className="absolute pointer-events-none"
              style={{
                left: `${tickRatio * 100}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '4px',
                height: '8px',
                background: isFilled ? '#4c6ef4' : '#3a3a3a',
              }}
            />
          );
        })}
        {/* Thumb — small square */}
        <div
          className="absolute cursor-grab active:cursor-grabbing z-10"
          style={{
            left: `${ratio * 100}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '10px',
            height: '10px',
            background: '#4c6ef4',
            border: '2px solid #6b8af7',
            touchAction: 'none',
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Leverage label above thumb */}
          <div
            style={{
              position: 'absolute',
              bottom: '14px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#4c6ef4',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              padding: '1px 4px',
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              lineHeight: '14px',
            }}
          >
            {leverage}x
          </div>
        </div>
      </div>
      {/* Min / Max labels */}
      <div className="flex justify-between mt-1" style={{ fontSize: '10px', color: '#6b7280' }}>
        <span>1x</span>
        <span>50x</span>
      </div>
    </div>
  );
}

// ─── Hyperliquid order signing helpers ───────────────────────────────────────

const HL_EXCHANGE_URL = 'https://api.hyperliquid.xyz/exchange';
const HL_INFO_URL = 'https://api.hyperliquid.xyz/info';

/** Fetch the asset index for a coin from Hyperliquid meta */
async function fetchAssetIndex(coin: string): Promise<number> {
  const res = await fetch(HL_INFO_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'meta' }),
  });
  if (!res.ok) throw new Error('Failed to fetch meta');
  const data = await res.json();
  const universe: { name: string }[] = data?.universe ?? [];
  const idx = universe.findIndex((u) => u.name === coin);
  if (idx === -1) throw new Error(`Asset not found: ${coin}`);
  return idx;
}

/** Remove trailing zeros from a numeric string (Hyperliquid requirement) */
function stripTrailingZeros(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
}

/**
 * Sign and submit a Hyperliquid L1 order action via MetaMask EIP-712.
 *
 * Signing flow (from official docs):
 * 1. Msgpack-encode the action object (field order matters)
 * 2. Append vault marker (0x00 for no vault) + nonce as uint64 big-endian
 * 3. Keccak-256 hash → connectionId (bytes32)
 * 4. Sign EIP-712 Agent { source: "a", connectionId } with domain chainId=1337
 * 5. POST { action, nonce, signature: {r,s,v}, vaultAddress: null }
 */
async function placeHyperliquidOrder(params: {
  walletAddress: string;
  coin: string;
  isBuy: boolean;
  limitPx: number;
  sz: number;
  orderType: 'Market' | 'Limit' | 'Pro';
  reduceOnly: boolean;
  leverage: number;
  marginMode: 'cross' | 'isolated';
}): Promise<{ success: boolean; message: string; data?: unknown }> {
  const ethereum = (window as any).ethereum;
  if (!ethereum) throw new Error('MetaMask not available');

  // 1. Resolve asset index
  const assetIndex = await fetchAssetIndex(params.coin);

  const nonce = Date.now();

  // 2. Format price and size — strip trailing zeros (Hyperliquid requirement)
  const pxStr = stripTrailingZeros(params.limitPx.toFixed(8));
  const szStr = stripTrailingZeros(params.sz.toFixed(8));

  // 3. Build order action — field order matters for msgpack encoding
  const orderEntry = {
    a: assetIndex,
    b: params.isBuy,
    p: pxStr,
    s: szStr,
    r: params.reduceOnly,
    t: params.orderType === 'Market'
      ? { limit: { tif: 'Ioc' } }  // Market = IOC limit at slippage price
      : { limit: { tif: 'Gtc' } },
    c: null,
  };

  const orderAction = {
    type: 'order',
    orders: [orderEntry],
    grouping: 'na',
  };

  // 4. Compute connectionId = keccak256(msgpack(action) + 0x00 + nonce_uint64_be)
  const connectionId = await computeConnectionId(orderAction, nonce, null);

  // 5. Sign EIP-712 Agent message with chainId=1337 (hardcoded by Hyperliquid)
  const domain = {
    name: 'Exchange',
    version: '1',
    chainId: 1337,
    verifyingContract: '0x0000000000000000000000000000000000000000',
  };

  const types = {
    Agent: [
      { name: 'source', type: 'string' },
      { name: 'connectionId', type: 'bytes32' },
    ],
  };

  const agentMessage = {
    source: 'a', // 'a' = mainnet, 'b' = testnet
    connectionId,
  };

  const sigHex: string = await ethereum.request({
    method: 'eth_signTypedData_v4',
    params: [
      params.walletAddress,
      JSON.stringify({
        domain,
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          ...types,
        },
        primaryType: 'Agent',
        message: agentMessage,
      }),
    ],
  });

  const signature = splitSignature(sigHex);

  // 6. Submit to exchange
  const body = {
    action: orderAction,
    nonce,
    signature,
    vaultAddress: null,
  };

  const res = await fetch(HL_EXCHANGE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await res.json();

  if (data?.status === 'ok') {
    const statuses = data?.response?.data?.statuses ?? [];
    const first = statuses[0];
    if (first?.error) {
      return { success: false, message: first.error, data };
    }
    return { success: true, message: 'Order placed successfully', data };
  } else {
    const errMsg = data?.response?.data?.statuses?.[0]?.error ?? data?.response ?? 'Order failed';
    return { success: false, message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), data };
  }
}

// ─── Main TradingPanel component ─────────────────────────────────────────────

export default function TradingPanel({ symbol, currentPrice, externalPrice, externalSize, externalSide, externalOrderType, sltp, onSltpChange, onTradePlaced, usdcBalance }: TradingPanelProps) {
  const { isConnected, walletAddress, walletType } = useWallet();
  const [orderType, setOrderType] = useState<OrderType>('Limit');
  const [side, setSide] = useState<Side>('Buy');
  const [price, setPrice] = useState(currentPrice);
  const [size, setSize] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [postOnly, setPostOnly] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leverage, setLeverage] = useState(10);
  const [marginMode, setMarginMode] = useState<MarginMode>('Cross');
  const [hiddenOrder, setHiddenOrder] = useState(false);
  const [tpSlEnabled, setTpSlEnabled] = useState(false);
  const [gtcMode, setGtcMode] = useState('GTC');
  const availableBalance = usdcBalance !== null && usdcBalance !== undefined ? usdcBalance : 0;
  const base = symbol.split('/')[0];

  // Local SL/TP state (synced with parent)
  const slEnabled = sltp?.slEnabled ?? false;
  const slPrice = sltp?.slPrice ?? '';
  const tpEnabled = sltp?.tpEnabled ?? false;
  const tpPrice = sltp?.tpPrice ?? '';

  const updateSltp = (patch: Partial<SLTPState>) => {
    if (onSltpChange) {
      onSltpChange({ slEnabled, slPrice, tpEnabled, tpPrice, ...patch });
    }
  };

  useEffect(() => {
    if (!externalPrice) setPrice(currentPrice);
  }, [currentPrice, externalPrice]);

  useEffect(() => {
    if (externalPrice) { setPrice(externalPrice); setOrderType('Limit'); }
  }, [externalPrice]);

  useEffect(() => {
    if (externalSize) setSize(externalSize);
  }, [externalSize]);

  useEffect(() => {
    if (externalSide) setSide(externalSide);
  }, [externalSide]);

  useEffect(() => {
    if (externalOrderType) setOrderType(externalOrderType);
  }, [externalOrderType]);

  const numericPrice = parseFloat(price.replace(',', '') || '0');
  const numericSize = parseFloat(size || '0');
  const orderValue = size && price ? (numericSize * numericPrice).toFixed(2) : '0.00';
  const positionValue = numericSize * numericPrice;
  const marginRequired = positionValue > 0 ? (positionValue / leverage).toFixed(2) : '0.00';

  // Insufficient balance check: margin required > available balance
  const marginRequiredNum = parseFloat(marginRequired);
  const insufficientBalance = isConnected && availableBalance > 0 && marginRequiredNum > availableBalance && numericSize > 0;

  const liquidationPrice = (() => {
    if (!numericSize || !numericPrice) return '—';
    const maintenanceMarginRate = 0.005;
    if (side === 'Buy') {
      const liqPrice = numericPrice * (1 - (1 / leverage) + maintenanceMarginRate);
      return liqPrice > 0 ? liqPrice.toFixed(2) : '—';
    } else {
      const liqPrice = numericPrice * (1 + (1 / leverage) - maintenanceMarginRate);
      return liqPrice.toFixed(2);
    }
  })();

  const handlePercentage = (pct: number) => {
    setPercentage(pct);
    const maxMargin = availableBalance * (pct / 100);
    const maxPositionValue = maxMargin * leverage;
    const maxSize = maxPositionValue / (numericPrice || 1);
    setSize(maxSize.toFixed(4));
  };

  const handlePlaceOrder = async () => {
    if (!size || parseFloat(size) <= 0) return;
    setOrderError(null);

    if (!isConnected || !walletAddress) {
      setOrderError('Connect your wallet to place orders.');
      return;
    }

    if (walletType !== 'metamask') {
      setOrderError('Hyperliquid orders require MetaMask (EIP-712 signing).');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await placeHyperliquidOrder({
        walletAddress,
        coin: base,
        isBuy: side === 'Buy',
        limitPx: orderType === 'Market' ? (side === 'Buy' ? numericPrice * 1.05 : numericPrice * 0.95) : numericPrice,
        sz: numericSize,
        orderType,
        reduceOnly,
        leverage,
        marginMode: marginMode === 'Cross' ? 'cross' : 'isolated',
      });

      if (result.success) {
        setOrderPlaced(true);
        if (onTradePlaced) {
          onTradePlaced({
            id: `trade-${Date.now()}`,
            side,
            price: numericPrice,
            size: numericSize,
            symbol,
            timestamp: Date.now(),
          });
        }
        setTimeout(() => {
          setOrderPlaced(false);
          setSize('');
          setPercentage(0);
        }, 2500);
      } else {
        setOrderError(result.message);
      }
    } catch (err: any) {
      if (err.code === 4001 || err.message?.includes('rejected')) {
        setOrderError('Signature rejected. Please approve the signing request in MetaMask.');
      } else {
        setOrderError(err.message || 'Failed to place order. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#1a1a1a] overflow-hidden">
      {/* Order Type Tabs */}
      <div className="flex border-b border-[#2a2a2a] bg-[#1e1e1e]">
        {(['Market', 'Limit', 'Pro'] as OrderType[]).map(type => (
          <button
            key={type}
            onClick={() => setOrderType(type)}
            className={`flex-1 py-2.5 text-sm font-medium transition-all duration-150 ${
              orderType === type
                ? 'text-[#4c6ef4] border-b-2 border-[#4c6ef4] bg-[rgba(76,110,244,0.08)]'
                : 'text-[#94A3B8] hover:text-[#F1F5F9]'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3">
        {/* Buy/Sell Toggle */}
        <div className="grid grid-cols-2 gap-0 border border-[#2a2a2a]">
          <button
            onClick={() => setSide('Buy')}
            className={`py-2 text-sm font-semibold transition-all duration-200 ${
              side === 'Buy' ? 'bg-[#22C55E] text-white' : 'text-[#94A3B8] hover:text-[#22C55E] bg-[#242424]'
            }`}
          >
            Buy / Long
          </button>
          <button
            onClick={() => setSide('Sell')}
            className={`py-2 text-sm font-semibold transition-all duration-200 border-l border-[#2a2a2a] ${
              side === 'Sell' ? 'bg-[#EF4444] text-white' : 'text-[#94A3B8] hover:text-[#EF4444] bg-[#242424]'
            }`}
          >
            Sell / Short
          </button>
        </div>

        {/* Margin Mode + Available Balance */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0 border border-[#2a2a2a]">
            {(['Cross', 'Isolated'] as MarginMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setMarginMode(mode)}
                className={`px-2.5 py-1 text-xs font-medium transition-all duration-150 ${
                  marginMode === mode
                    ? 'bg-[rgba(76,110,244,0.2)] text-[#4c6ef4]'
                    : 'text-[#94A3B8] hover:text-[#F1F5F9] bg-[#242424]'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-[10px] text-[#94A3B8]">Available</span>
            <span className={`font-mono text-xs tabular-nums ${usdcBalance !== null && usdcBalance !== undefined ? 'text-[#22C55E]' : 'text-[#94A3B8]'}`}>
              {usdcBalance !== null && usdcBalance !== undefined
                ? `${usdcBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC`
                : '— USDC'}
            </span>
          </div>
        </div>

        {/* Price Input (Limit/Pro) */}
        {orderType !== 'Market' && (
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1">Price (USDC)</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#242424] border border-[#2a2a2a] focus-within:border-[#4c6ef4] transition-colors">
              <input
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                className="flex-1 bg-transparent text-sm font-mono text-[#F1F5F9] outline-none tabular-nums"
                placeholder="0.00"
              />
              <span className="text-xs text-[#94A3B8]">USDC</span>
            </div>
          </div>
        )}

        {/* Size Input */}
        <div>
          <label className="block text-xs text-[#94A3B8] mb-1">Size ({base})</label>
          <div className="flex items-center gap-2 px-3 py-2 bg-[#242424] border border-[#2a2a2a] focus-within:border-[#4c6ef4] transition-colors">
            <input
              type="number"
              value={size}
              onChange={e => { setSize(e.target.value); setPercentage(0); }}
              className="flex-1 bg-transparent text-sm font-mono text-[#F1F5F9] outline-none tabular-nums"
              placeholder="0.0000"
            />
            <span className="text-xs text-[#94A3B8]">{base}</span>
          </div>
        </div>

        {/* Percentage Buttons */}
        <div>
          <div className="flex items-center justify-between mb-2 gap-1">
            {PERCENTAGES.map(pct => (
              <button
                key={pct}
                onClick={() => handlePercentage(pct)}
                className={`flex-1 py-1 text-xs font-medium transition-all duration-150 ${
                  percentage === pct
                    ? side === 'Buy' ? 'bg-[rgba(34,197,94,0.2)] text-[#22C55E] border border-[rgba(34,197,94,0.4)]' : 'bg-[rgba(239,68,68,0.2)] text-[#EF4444] border border-[rgba(239,68,68,0.4)]'
                    : 'bg-[#242424] text-[#94A3B8] border border-[#2a2a2a] hover:border-[#4c6ef4]'
                }`}
              >
                {pct}%
              </button>
            ))}
          </div>
          <div className="relative h-1 bg-[#242424]">
            <div
              className={`absolute inset-y-0 left-0 transition-all duration-300 ${
                side === 'Buy' ? 'bg-[#22C55E]' : 'bg-[#EF4444]'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        {/* ─── Clean Leverage Slider ─── */}
        <CleanLeverageSlider leverage={leverage} onChange={setLeverage} />

        {/* ─── TP/SL Checkbox Row ─── */}
        <div className="space-y-0">
          {/* TP/SL row */}
          <div className="flex items-center gap-2 py-1.5">
            <input
              type="checkbox"
              id="tpsl-checkbox"
              checked={tpSlEnabled}
              onChange={e => {
                setTpSlEnabled(e.target.checked);
                if (!e.target.checked) {
                  updateSltp({ tpEnabled: false, slEnabled: false, tpPrice: '', slPrice: '' });
                } else {
                  updateSltp({ tpEnabled: true, slEnabled: true });
                }
              }}
              className="w-3.5 h-3.5 shrink-0 cursor-pointer accent-[#4c6ef4]"
              style={{ accentColor: '#4c6ef4' }}
            />
            <label htmlFor="tpsl-checkbox" className="text-xs text-[#94A3B8] cursor-pointer select-none">TP/SL</label>
          </div>

          {/* Expanded TP/SL inputs */}
          {tpSlEnabled && (
            <div className="flex gap-2 pb-1.5">
              <div className="flex-1 flex items-center gap-1 px-2 py-1.5 bg-[#242424] border border-[#2a2a2a] focus-within:border-[#22C55E] transition-colors">
                <input
                  type="number"
                  value={tpPrice}
                  onChange={e => updateSltp({ tpPrice: e.target.value, tpEnabled: true })}
                  className="flex-1 min-w-0 bg-transparent text-xs font-mono text-[#F1F5F9] outline-none tabular-nums"
                  placeholder={numericPrice ? (side === 'Buy' ? (numericPrice * 1.05).toFixed(2) : (numericPrice * 0.95).toFixed(2)) : 'Take Profit'}
                />
                <span className="text-[10px] text-[#94A3B8] shrink-0">TP</span>
              </div>
              <div className="flex-1 flex items-center gap-1 px-2 py-1.5 bg-[#242424] border border-[#2a2a2a] focus-within:border-[#EF4444] transition-colors">
                <input
                  type="number"
                  value={slPrice}
                  onChange={e => updateSltp({ slPrice: e.target.value, slEnabled: true })}
                  className="flex-1 min-w-0 bg-transparent text-xs font-mono text-[#F1F5F9] outline-none tabular-nums"
                  placeholder={numericPrice ? (side === 'Buy' ? (numericPrice * 0.97).toFixed(2) : (numericPrice * 1.03).toFixed(2)) : 'Stop Loss'}
                />
                <span className="text-[10px] text-[#94A3B8] shrink-0">SL</span>
              </div>
            </div>
          )}

          {/* Hidden Order row */}
          <div className="flex items-center gap-2 py-1.5">
            <input
              type="checkbox"
              id="hidden-order-checkbox"
              checked={hiddenOrder}
              onChange={e => setHiddenOrder(e.target.checked)}
              className="w-3.5 h-3.5 shrink-0 cursor-pointer"
              style={{ accentColor: '#4c6ef4' }}
            />
            <label htmlFor="hidden-order-checkbox" className="text-xs text-[#94A3B8] cursor-pointer select-none">Hidden Order</label>
          </div>

          {/* Reduce-Only row with GTC dropdown */}
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="reduce-only-checkbox"
                checked={reduceOnly}
                onChange={e => setReduceOnly(e.target.checked)}
                className="w-3.5 h-3.5 shrink-0 cursor-pointer"
                style={{ accentColor: '#4c6ef4' }}
              />
              <label htmlFor="reduce-only-checkbox" className="text-xs text-[#94A3B8] cursor-pointer select-none">Reduce-Only</label>
            </div>
            <select
              value={gtcMode}
              onChange={e => setGtcMode(e.target.value)}
              className="text-xs text-[#94A3B8] bg-[#242424] border border-[#2a2a2a] px-1.5 py-0.5 outline-none cursor-pointer hover:border-[#4c6ef4] transition-colors"
            >
              <option value="GTC">GTC</option>
              <option value="IOC">IOC</option>
              <option value="FOK">FOK</option>
              <option value="GTD">GTD</option>
            </select>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-[#242424] border border-[#2a2a2a] p-3 space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-[#94A3B8]">Order Value</span>
            <span className="font-mono text-[#F1F5F9] tabular-nums">${parseFloat(orderValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[#94A3B8]">Margin Required</span>
            <span className="font-mono text-[#4c6ef4] tabular-nums">${parseFloat(marginRequired).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[#94A3B8]">Liquidation Price</span>
            <span className={`font-mono tabular-nums ${liquidationPrice === '—' ? 'text-[#94A3B8]' : side === 'Buy' ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>
              {liquidationPrice === '—' ? '—' : `$${liquidationPrice}`}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-[#94A3B8]">Est. Fee (0.02%)</span>
            <span className="font-mono text-[#F1F5F9] tabular-nums">${(parseFloat(orderValue) * 0.0002).toFixed(4)}</span>
          </div>
          {orderType === 'Market' && (
            <div className="flex justify-between text-xs">
              <span className="text-[#94A3B8]">Slippage</span>
              <span className="font-mono text-[#4c6ef4] tabular-nums">~0.05%</span>
            </div>
          )}
        </div>

        {/* Wallet status / error */}
        {!isConnected && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[rgba(76,110,244,0.08)] border border-[rgba(76,110,244,0.3)] text-xs text-[#94A3B8]">
            <Icon name="ExclamationCircleIcon" size={14} variant="outline" />
            <span>Connect wallet to place live orders</span>
          </div>
        )}
        {isConnected && walletType !== 'metamask' && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[rgba(234,179,8,0.08)] border border-[rgba(234,179,8,0.3)] text-xs text-[#EAB308]">
            <Icon name="ExclamationTriangleIcon" size={14} variant="outline" />
            <span>MetaMask required for order signing</span>
          </div>
        )}
        {insufficientBalance && (
          <div className="flex items-start gap-2 px-3 py-2 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.3)] text-xs text-[#EF4444]">
            <Icon name="XCircleIcon" size={14} variant="outline" />
            <span>Insufficient balance — margin required (${marginRequiredNum.toLocaleString('en-US', { minimumFractionDigits: 2 })}) exceeds available USDC (${availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
          </div>
        )}
        {orderError && (
          <div className="flex items-start gap-2 px-3 py-2 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.3)] text-xs text-[#EF4444]">
            <Icon name="XCircleIcon" size={14} variant="outline" />
            <span>{orderError}</span>
          </div>
        )}

        {/* Place Order Button */}
        <button
          onClick={handlePlaceOrder}
          disabled={!size || parseFloat(size) <= 0 || orderPlaced || isSubmitting || insufficientBalance}
          className={`w-full py-3 font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
            orderPlaced
              ? 'bg-[#22C55E] text-white'
              : side === 'Buy' ? 'bg-[#22C55E] text-white hover:opacity-90' : 'bg-[#EF4444] text-white hover:opacity-90'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Signing & Submitting…
            </span>
          ) : orderPlaced ? (
            <span className="flex items-center justify-center gap-2">
              <Icon name="CheckCircleIcon" size={16} variant="solid" />
              Order Placed!
            </span>
          ) : !isConnected ? (
            'Connect Wallet to Trade'
          ) : (
            `${side === 'Buy' ? 'Buy' : 'Sell'} ${base} ${leverage}x ${orderType === 'Market' ? 'at Market' : `@ $${price}`}`
          )}
        </button>
      </div>
    </div>
  );
}